import React, { FunctionComponent, useState } from 'react'
import { Spinner } from 'reactstrap'

import Error from 'components/Toolkit/Text/Error'
import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { ActionButton } from 'components/TailwindUIToolkit'
import { Sidepeek } from 'ui'
import {
  useGetPortfolioHomeWithFeaturesQuery,
  GetPortfolioHomeWithFeaturesQuery
} from 'graphql/generated'
import EditFeatures from '../Components/Features/EditFeaturesForm'
import FeaturesGrid from '../Grids/FeaturesGrid'

type FeaturesPropTypes = {
  portfolioHomeId: string
}

const formatFeatures = (features: GetPortfolioHomeWithFeaturesQuery['portfolio_homes_by_pk']) => {
  const communityFeatures = features!.community?.community_features
  const internalFeatures = features!.portfolio_home_internal_features
  const externalFeatures = features!.portfolio_home_external_features
  return {
    community: {
      name: features!.community?.name ?? null,
      hoaContactName: communityFeatures?.hoa_contact_name ?? null,
      hoaContactEmail: communityFeatures?.hoa_contact_email ?? null,
      amenityFee: communityFeatures?.amenity_fee ?? null,
      amenityDetails: communityFeatures?.amenity_details ?? null,
      pool: communityFeatures?.pool ?? null,
      playground: communityFeatures?.playground ?? null,
      landscaping: communityFeatures?.landscaping ?? null,
      security: communityFeatures?.security ?? null,
      dogPark: communityFeatures?.dog_park ?? null,
      nature: communityFeatures?.nature ?? null
    },
    internal: {
      washerDryer: internalFeatures?.washer_dryer ?? null,
      basement: internalFeatures?.basement ?? null,
      warranties: internalFeatures?.warranties ?? null,
      appliances: internalFeatures?.appliances ?? null,
      utilitiesException: internalFeatures?.utilities_exception ?? null
    },
    external: {
      security: externalFeatures?.security ?? '',
      mailboxInfo: externalFeatures?.mailbox_info ?? null,
      garage: externalFeatures?.garage ?? null,
      parking: externalFeatures?.parking ?? null,
      gate: externalFeatures?.gate ?? null,
      fence: externalFeatures?.fence ?? null,
      shed: externalFeatures?.shed ?? null
    }
  }
}

const Features: FunctionComponent<React.PropsWithChildren<FeaturesPropTypes>> = ({
  portfolioHomeId
}) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)

  const [{ data, error }] = useGetPortfolioHomeWithFeaturesQuery({
    variables: { portfolioHomeId }
  })
  if (error) {
    return <Error>{error.message}</Error>
  }

  const homeFeatures = data?.portfolio_homes_by_pk
  if (!homeFeatures) {
    return <Error>could not find data</Error>
  }

  const portfolioHomeFeatures = formatFeatures(homeFeatures)
  const editButton = (
    <ActionButton onClick={() => setDrawerOpen(true)}>
      + Edit
      <Sidepeek isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title='Edit Home Features'>
        <EditFeatures
          portfolioHomeId={portfolioHomeId}
          communityId={homeFeatures.community_id}
          portfolioHomeFeatures={portfolioHomeFeatures}
        />
      </Sidepeek>
    </ActionButton>
  )

  return (
    <ContentSectionCard title='Features' size='fit-content' padding action={editButton}>
      <React.Suspense fallback={<Spinner />}>
        <FeaturesGrid portfolioHomeFeatures={portfolioHomeFeatures} />
      </React.Suspense>
    </ContentSectionCard>
  )
}

export default Features
